import React, { useState, useEffect } from 'react';
import { Calendar, Users, Home, Clock, TrendingUp, FileText, LogOut, RefreshCw } from 'lucide-react';
import API_BASE_URL from '../../../config/baseApi';
import ReportsModal from './ReportsModal';

const API_TIMEOUT = 30000;

export default function Dashboard() {
  const [currentDate, setCurrentDate] = useState(new Date(2026, 0, 19));
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isReportsModalOpen, setIsReportsModalOpen] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState('reservation');
  
  // Fetch dashboard data from backend
  useEffect(() => {
    fetchDashboardData();
  }, []);
  
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);
      
      const response = await fetch(`${API_BASE_URL}/api/dashboard`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          // Add authentication header if needed
          // 'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setDashboardData(result.data);
        setError(null);
      } else {
        throw new Error(result.message || 'Failed to load data');
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('Request timeout - please try again');
      } else {
        setError(err.message);
      }
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const openReportsModal = (reportType = 'reservation') => {
  setSelectedReportType(reportType);
  setIsReportsModalOpen(true);
  };
  // Navigate to checkout page
  const handleGuestClick = (guest) => {
    if (guest.hash) {
      // If hash is available, navigate to checkout page
      window.location.href = `/admin/check-out/${guest.hash}`;
    } else if (guest.id) {
      // If only ID is available, you might need to generate hash first
      // Or construct the URL differently based on your routing
      console.warn('Guest hash not available, using ID:', guest.id);
      window.location.href = `/checkout/${guest.id}`;
    } else {
      console.error('Cannot navigate: No hash or ID available for guest');
    }
  };

  const handleReservationView = (reservation) => {
    window.location.href = `/admin/check-in?reservation_id=${reservation.id}`;
  };

  
  const daysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };
  
  const firstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };
  
  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
  
  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };
  
  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };
  
  const renderCalendar = () => {
    const totalDays = daysInMonth(currentDate);
    const firstDay = firstDayOfMonth(currentDate);
    const days = [];
    
    const prevMonthDays = daysInMonth(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push(
        <div key={`prev-${i}`} className="p-2 text-center text-gray-400 dark:text-gray-600 text-sm">
          {prevMonthDays - i}
        </div>
      );
    }
    
    for (let day = 1; day <= totalDays; day++) {
      const isToday = day === 19;
      days.push(
        <div
          key={day}
          className={`p-2 text-center cursor-pointer rounded-lg transition-all text-sm font-medium ${
            isToday
              ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg scale-105'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:scale-105'
          }`}
        >
          {day}
        </div>
      );
    }
    
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push(
        <div key={`next-${i}`} className="p-2 text-center text-gray-400 dark:text-gray-600 text-sm">
          {i}
        </div>
      );
    }
    
    return days;
  };

  const StatCard = ({ icon: Icon, label, value, trend, color = "blue" }) => {
    const colorClasses = {
      blue: "from-blue-500 to-blue-600",
      purple: "from-purple-500 to-purple-600",
      green: "from-green-500 to-green-600",
      orange: "from-orange-500 to-orange-600"
    };
    
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">{label}</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">{value}</p>
            {trend && (
              <p className="text-xs text-green-600 dark:text-green-400 mt-2 flex items-center">
                <TrendingUp className="w-3 h-3 mr-1" />
                {trend}
              </p>
            )}
          </div>
          <div className={`p-3 rounded-xl bg-gradient-to-br ${colorClasses[color]} shadow-lg`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg max-w-md">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 dark:text-red-400 text-2xl">⚠️</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Error Loading Data</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button 
            onClick={fetchDashboardData}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Extract data with safe fallbacks
  const rate = dashboardData?.rate?.rate || '291.00';
  const availableRooms = dashboardData?.availableRooms || [];
  const availableRoomsCount = dashboardData?.availableRoomsCount || 0;
  const todayReservations = dashboardData?.todayReservations || [];
  const reservationCount = dashboardData?.reservationCount || 0;
  const currentRegistrations = dashboardData?.currentRegistrations || [];
  const totalPaxCount = dashboardData?.totalPaxCount || 0;
  const checkoutCount = dashboardData?.checkoutCount || 0;
  const mealCounts = dashboardData?.mealCounts || { today: { reserved: {}, registered: {} }, tomorrow: { reserved: {}, registered: {} } };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 rounded-lg">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10 shadow-sm bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-2 bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-lg">
                <Calendar className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {dashboardData?.currentDate || 'January 19, 2026'}
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button 
                onClick={fetchDashboardData}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Refresh data"
              >
                <RefreshCw className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </button>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 px-4 py-2 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <span className="text-xs text-yellow-700 dark:text-yellow-400 font-medium">USD Rate:</span>
                <span className="ml-2 text-sm font-bold text-yellow-600 dark:text-yellow-300">${rate}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <button className="flex items-center justify-center space-x-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1" onClick={() => window.location.href = '/admin/reservations'}>
            <Calendar className="w-5 h-5" />
            <span>New Reservation</span>
          </button>
          <button className="flex items-center justify-center space-x-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1" onClick={() => window.location.href = '/admin/check-in'}>
            <Users className="w-5 h-5" />
            <span>Registration</span>
          </button>
          <button className="flex items-center justify-center space-x-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1" onClick={() => window.location.href = '/admin/billing'}>
            <FileText className="w-5 h-5" />
            <span>Billing</span>
          </button>
          <button className="flex items-center justify-center space-x-3 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1" onClick={() => window.location.href = '/admin/transactions'}>
            <LogOut className="w-5 h-5" />
            <span>Day Transaction</span>
          </button>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard icon={LogOut} label="Checkout Today" value={checkoutCount} color="blue" />
          <StatCard icon={Home} label="Available Rooms" value={availableRoomsCount} color="green" />
          <StatCard icon={Users} label="In House" value={currentRegistrations.length} color="purple" />
          <StatCard icon={Users} label="Total Guests" value={totalPaxCount} color="orange" />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - 2/3 width */}
          <div className="lg:col-span-2 space-y-6">
            {/* Available Rooms */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4">
                <h2 className="text-lg font-bold text-white flex items-center">
                  <Home className="w-5 h-5 mr-2" />
                  Available Rooms ({availableRoomsCount})
                </h2>
              </div>
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full">
                  <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700/50 z-10">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Room</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {availableRooms.length > 0 ? (
                      availableRooms.map((room, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mr-3">
                                <span className="text-sm font-bold text-blue-600 dark:text-blue-400">{room.room_no}</span>
                              </div>
                              <span className="text-sm font-medium text-gray-900 dark:text-white">Room {room.room_no}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                              {room.room_type || 'STANDARD'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium text-sm">
                              Allocate →
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                          No available rooms
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Reservations & In House */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Reservations */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4">
                  <h2 className="text-lg font-bold text-white flex items-center">
                    <Calendar className="w-5 h-5 mr-2" />
                    Reservations ({reservationCount})
                  </h2>
                </div>
                <div className="p-6 max-h-80 overflow-y-auto">
                  <div className="space-y-3">
                    {todayReservations.length > 0 ? (
                      todayReservations.map((reservation, idx) => (
                        <div key={idx} className="flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                          <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">{reservation.guest_name}</p>
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              Time: {reservation.reservation_time}
                            </p>
                          </div>
                          <button className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-lg transition-colors" onClick={() => handleReservationView(reservation)}>
                            View
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-gray-500 dark:text-gray-400 py-4">No reservations today</p>
                    )}
                  </div>
                </div>
              </div>

              {/* In House Guests */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
                  <h2 className="text-lg font-bold text-white flex items-center">
                    <Clock className="w-5 h-5 mr-2" />
                    In House Guests
                  </h2>
                </div>
                <div className="p-6 max-h-80 overflow-y-auto">
                  <div className="space-y-3">
                    {currentRegistrations.length > 0 ? (
                      currentRegistrations.map((guest, idx) => (
                        <div 
                          key={idx} 
                          onClick={() => handleGuestClick(guest)}
                          className="flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800 cursor-pointer hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 rounded-lg bg-orange-600 flex items-center justify-center text-white text-xs font-bold">
                              {guest.room_no || guest.allocated_room_no}
                            </div>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{guest.guest_name}</span>
                          </div>
                          <button className="text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-300 text-xs font-semibold">
                            View →
                          </button>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-gray-500 dark:text-gray-400 py-4">No guests in house</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Meal Planning */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                <h2 className="text-lg font-bold text-white">Meal Planning</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-200 dark:divide-gray-700">
                {/* Today */}
                <div className="p-6">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                    Today
                  </h3>
                  <div className="space-y-3">
                    {[
                      { meal: 'Breakfast', registered: mealCounts.today.registered?.breakfast_count || 0, reservation: mealCounts.today.reserved?.breakfast_count || 0 },
                      { meal: 'Lunch', registered: mealCounts.today.registered?.lunch_count || 0, reservation: mealCounts.today.reserved?.lunch_count || 0 },
                      { meal: 'Dinner', registered: mealCounts.today.registered?.dinner_count || 0, reservation: mealCounts.today.reserved?.dinner_count || 0 }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.meal}</span>
                        <div className="flex items-center space-x-4">
                          <span className="text-xs text-gray-600 dark:text-gray-400">Reg: <strong>{item.registered}</strong></span>
                          <span className="text-xs text-gray-600 dark:text-gray-400">Res: <strong>{item.reservation}</strong></span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tomorrow */}
                <div className="p-6">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                    <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                    Tomorrow
                  </h3>
                  <div className="space-y-3">
                    {[
                      { meal: 'Breakfast', registered: mealCounts.tomorrow.registered?.breakfast_count || 0, reservation: mealCounts.tomorrow.reserved?.breakfast_count || 0 },
                      { meal: 'Lunch', registered: mealCounts.tomorrow.registered?.lunch_count || 0, reservation: mealCounts.tomorrow.reserved?.lunch_count || 0 },
                      { meal: 'Dinner', registered: mealCounts.tomorrow.registered?.dinner_count || 0, reservation: mealCounts.tomorrow.reserved?.dinner_count || 0 }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.meal}</span>
                        <div className="flex items-center space-x-4">
                          <span className="text-xs text-gray-600 dark:text-gray-400">Reg: <strong>{item.registered}</strong></span>
                          <span className="text-xs text-gray-600 dark:text-gray-400">Res: <strong>{item.reservation}</strong></span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - 1/3 width */}
          <div className="space-y-6">
            {/* Calendar */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <button onClick={prevMonth} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <h3 className="text-lg font-bold text-white">
                    {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </h3>
                  <button onClick={nextMonth} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
                    <div key={idx} className="text-center text-xs font-bold text-gray-600 dark:text-gray-400 py-2">
                      {day}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {renderCalendar()}
                </div>
              </div>
            </div>

            {/* Quick Reports */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4">
                <h2 className="text-lg font-bold text-white flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  Quick Reports
                </h2>
              </div>
              <div className="p-4 space-y-2">
                {[
                  { name: 'Reservation Report', type: 'reservation' },
                  { name: 'Registration Report', type: 'registration' },
                  { name: 'Summary Report', type: 'summary' },
                  { name: 'Day End Report', type: 'day-end' },
                  { name: 'Transactions Report', type: 'transaction' }
                ].map((report, idx) => (
                  <button 
                    key={idx} 
                    onClick={() => openReportsModal(report.type)}
                    className="w-full text-left px-4 py-3 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors group"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{report.name}</span>
                      <span className="text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">→</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
          <p className="text-center text-sm text-gray-600 dark:text-gray-400">
            Copyright © 2026. All rights reserved.
          </p>
        </div>
      </div>
      <ReportsModal 
        isOpen={isReportsModalOpen}
        onClose={() => setIsReportsModalOpen(false)}
        initialReportType={selectedReportType}
      />
    </div>
  );
}